import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WorkspaceDatePicker as DatePicker } from "./workspace/WorkspaceDatePicker";
import {
  staffingDeals, getDealPlacement, addPlacementCandidate,
  updatePlacementCandidate, removePlacementCandidate, listCandidates, fmtMoney,
} from "@/lib/api";
import {
  Loader2, Briefcase, Trophy, Users, Search, Plus, Trash2, Star,
  Sparkles, UserCheck, Calendar as CalendarIcon, X,
} from "lucide-react";

const STAGE_COLOR = {
  Matched: "bg-white/10 text-white/70 border-white/15",
  Shortlisted: "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30",
  Interviewing: "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/30",
  Selected: "bg-[#F5B94A]/15 text-[#F5B94A] border-[#F5B94A]/30",
  Placed: "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30",
};

const matchColor = (n) =>
  n >= 80 ? "text-[#2EE59D]" : n >= 60 ? "text-[#66B2FF]" : n >= 40 ? "text-[#F5B94A]" : "text-white/50";

const StatCard = ({ label, value, color = "text-white", icon: Icon }) => (
  <div className="rounded-xl border border-white/10 bg-[#0A0F1E] p-4">
    <div className="flex items-center gap-2 text-white/45 text-xs mb-1">
      {Icon && <Icon size={13} />} {label}
    </div>
    <div className={`text-2xl font-semibold tabular-nums ${color}`}>{value}</div>
  </div>
);

// ---- Placement panel (per Won deal) ----------------------------------------
const PlacementPanel = ({ token, dealId, stages, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manualQ, setManualQ] = useState("");
  const [manualResults, setManualResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const load = useCallback(() => {
    getDealPlacement(token, dealId)
      .then(setData)
      .catch(() => toast.error("Failed to load placement"));
  }, [token, dealId]);

  useEffect(() => { load(); }, [load]);

  const refreshFrom = (res) => {
    setData(res);
    onChanged && onChanged();
  };

  const addCandidate = async (candidateId, stage) => {
    setBusy(true);
    try {
      const res = await addPlacementCandidate(token, dealId, { candidate_id: candidateId, stage });
      refreshFrom(res);
      toast.success("Candidate added to deal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add candidate");
    } finally { setBusy(false); }
  };

  const patchCandidate = async (candidateId, body) => {
    setBusy(true);
    try {
      const res = await updatePlacementCandidate(token, dealId, candidateId, body);
      refreshFrom(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const removeCandidate = async (candidateId) => {
    setBusy(true);
    try {
      const res = await removePlacementCandidate(token, dealId, candidateId);
      refreshFrom(res);
      toast.success("Candidate removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setBusy(false); }
  };

  const runManualSearch = async () => {
    if (!manualQ.trim()) { setManualResults([]); return; }
    setSearching(true);
    try {
      const res = await listCandidates(token, { search: manualQ.trim() });
      const added = new Set((data?.shortlist || []).map((s) => s.candidate_id));
      setManualResults((res.candidates || []).filter((c) => !added.has(c.id)).slice(0, 8));
    } catch {
      toast.error("Search failed");
    } finally { setSearching(false); }
  };

  const deal = data?.deal;
  const shortlist = data?.shortlist || [];
  const suggestions = data?.suggestions || [];

  return (
    <SheetContent side="right" className="w-full sm:max-w-2xl bg-[#080C17] border-white/10 p-0 overflow-hidden flex flex-col" data-testid="placement-panel">
      <SheetHeader className="px-5 py-4 border-b border-white/10">
        <SheetTitle className="text-white flex items-center gap-2">
          <Briefcase size={17} className="text-[#2EE59D]" />
          {deal ? `${deal.company || "Deal"} — Staffing` : "Staffing"}
        </SheetTitle>
        {deal && (
          <div className="text-white/50 text-sm flex flex-wrap gap-x-4 gap-y-1 mt-1">
            <span>{deal.contact || "—"}</span>
            <span className="text-[#2EE59D]">{fmtMoney(deal.monthly_value)}/mo</span>
            {deal.team_size ? <span>{deal.team_size} seats</span> : null}
            {deal.industry_label ? <span>{deal.industry_label}</span> : null}
          </div>
        )}
      </SheetHeader>

      {!data ? (
        <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-white/40" /></div>
      ) : (
        <ScrollArea className="flex-1">
          <div className="p-5 space-y-6">
            {/* Shortlist */}
            <section data-testid="placement-shortlist">
              <div className="flex items-center gap-2 text-white/70 text-sm font-medium mb-3">
                <UserCheck size={15} /> Shortlist ({shortlist.length})
              </div>
              {shortlist.length === 0 ? (
                <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-white/40 text-sm">
                  No candidates yet. Add from suggestions or search below.
                </div>
              ) : (
                <div className="space-y-3">
                  {shortlist.map((s) => {
                    const c = s.candidate || {};
                    return (
                      <div key={s.candidate_id} className="rounded-lg border border-white/10 bg-[#0E1424] p-3" data-testid="placement-shortlist-row">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-white font-medium truncate">{c.name || s.candidate_id}</div>
                            <div className="text-white/45 text-xs truncate">{c.current_position || "—"}{c.availability ? ` · ${c.availability}` : ""}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Select value={s.stage} onValueChange={(v) => patchCandidate(s.candidate_id, { stage: v })} disabled={busy}>
                              <SelectTrigger className="h-8 w-36 bg-[#0A0F1E] border-white/10 text-white text-xs" data-testid="placement-stage-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {stages.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-white/50 hover:text-[#FF5C7A] hover:bg-[#FF5C7A]/10"
                              onClick={() => removeCandidate(s.candidate_id)} disabled={busy}
                              data-testid="placement-remove-candidate" aria-label="Remove candidate">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                        <div className="mt-3 grid sm:grid-cols-2 gap-3">
                          <div>
                            <div className="text-white/40 text-[11px] mb-1 flex items-center gap-1"><CalendarIcon size={11} /> Start Date</div>
                            <DatePicker value={s.start_date || ""} onChange={(v) => patchCandidate(s.candidate_id, { start_date: v })}
                              testid="placement-start-date" placeholder="Set start date" />
                          </div>
                          <div>
                            <div className="text-white/40 text-[11px] mb-1">Notes</div>
                            <Input defaultValue={s.notes || ""} placeholder="Add a note"
                              onBlur={(e) => { if ((e.target.value || "") !== (s.notes || "")) patchCandidate(s.candidate_id, { notes: e.target.value }); }}
                              className="h-9 bg-[#0A0F1E] border-white/10 text-white text-sm" data-testid="placement-notes-input" />
                          </div>
                        </div>
                        {s.placement_date && (
                          <div className="mt-2 text-[#2EE59D] text-xs">Placed on {s.placement_date}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Suggestions */}
            <section data-testid="placement-suggestions">
              <div className="flex items-center gap-2 text-white/70 text-sm font-medium mb-3">
                <Sparkles size={15} className="text-[#A78BFA]" /> Suggested Matches
              </div>
              {!data.has_submission ? (
                <div className="text-white/40 text-sm">No linked assessment — use search below to add candidates manually.</div>
              ) : suggestions.length === 0 ? (
                <div className="text-white/40 text-sm">No further suggestions available.</div>
              ) : (
                <div className="space-y-2">
                  {suggestions.map((c) => (
                    <div key={c.candidate_id} className="rounded-lg border border-white/10 bg-[#0E1424] p-3 flex items-center justify-between gap-3" data-testid="placement-suggestion-row">
                      <div className="min-w-0">
                        <div className="text-white font-medium truncate">{c.name}</div>
                        <div className="text-white/45 text-xs truncate">{c.current_position || "—"}{c.availability ? ` · ${c.availability}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className={`text-sm font-semibold tabular-nums ${matchColor(c.overall)}`}>{c.overall}%</div>
                        <Button size="sm" variant="outline" className="h-8 border-white/15 text-white/80 hover:bg-[#2EE59D]/10 hover:text-[#2EE59D] hover:border-[#2EE59D]/40"
                          onClick={() => addCandidate(c.candidate_id, "Shortlisted")} disabled={busy}
                          data-testid="placement-add-suggestion">
                          <Plus size={13} className="mr-1" /> Add
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Manual add */}
            <section data-testid="placement-manual-add">
              <div className="flex items-center gap-2 text-white/70 text-sm font-medium mb-3">
                <Search size={15} /> Add a Candidate Manually
              </div>
              <div className="flex gap-2">
                <Input value={manualQ} onChange={(e) => setManualQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runManualSearch(); }}
                  placeholder="Search candidates by name, skill, position..."
                  className="h-10 bg-[#0A0F1E] border-white/10 text-white" data-testid="placement-manual-search-input" />
                <Button onClick={runManualSearch} disabled={searching} className="h-10 bg-[#1E6BFF] hover:bg-[#1E6BFF]/90" data-testid="placement-manual-search-btn">
                  {searching ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                </Button>
              </div>
              {manualResults.length > 0 && (
                <div className="mt-3 space-y-2">
                  {manualResults.map((c) => (
                    <div key={c.id} className="rounded-lg border border-white/10 bg-[#0E1424] p-2.5 flex items-center justify-between gap-3" data-testid="placement-manual-result">
                      <div className="min-w-0">
                        <div className="text-white text-sm truncate">{c.name}</div>
                        <div className="text-white/40 text-xs truncate">{c.current_position || "—"}</div>
                      </div>
                      <Button size="sm" variant="outline" className="h-8 border-white/15 text-white/80 hover:bg-white/5"
                        onClick={() => { addCandidate(c.id, "Shortlisted"); setManualResults((r) => r.filter((x) => x.id !== c.id)); }}
                        disabled={busy} data-testid="placement-manual-add-btn">
                        <Plus size={13} className="mr-1" /> Add
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </ScrollArea>
      )}
    </SheetContent>
  );
};

// ---- Staffing manager (Won deals) ------------------------------------------
export const StaffingManager = ({ token, openDealId = null }) => {
  const [data, setData] = useState(null);
  const [activeDeal, setActiveDeal] = useState(openDealId);

  const load = useCallback(() => {
    staffingDeals(token).then(setData).catch(() => toast.error("Failed to load staffing"));
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (openDealId) setActiveDeal(openDealId); }, [openDealId]);

  const totals = useMemo(() => {
    const deals = data?.deals || [];
    return {
      won: deals.length,
      placed: deals.reduce((s, d) => s + (d.placed_count || 0), 0),
      shortlisted: deals.reduce((s, d) => s + (d.shortlisted_count || 0), 0),
    };
  }, [data]);

  if (!data) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-white/50" /></div>;

  return (
    <div className="space-y-6" data-testid="staffing-manager">
      <div>
        <h2 className="text-lg font-semibold text-white">Staffing</h2>
        <p className="text-white/50 text-sm">Fulfill Won deals — shortlist, interview, select and place candidates.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Won Deals" value={totals.won} icon={Trophy} color="text-[#2EE59D]" />
        <StatCard label="Candidates Shortlisted" value={totals.shortlisted} icon={Users} color="text-[#66B2FF]" />
        <StatCard label="Placed" value={totals.placed} icon={UserCheck} color="text-[#2EE59D]" />
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/50">Company</TableHead>
              <TableHead className="text-white/50">Contact</TableHead>
              <TableHead className="text-white/50">Monthly</TableHead>
              <TableHead className="text-white/50">Industry</TableHead>
              <TableHead className="text-white/50">Assigned</TableHead>
              <TableHead className="text-white/50 text-right">Shortlisted</TableHead>
              <TableHead className="text-white/50 text-right">Placed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.deals.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-white/40" data-testid="staffing-empty">
                No Won deals yet. Mark a deal as Won on the Deal Board to begin staffing.
              </TableCell></TableRow>
            ) : data.deals.map((d) => (
              <TableRow key={d.id} className="border-white/10 hover:bg-white/5 cursor-pointer" data-testid="staffing-deal-row"
                onClick={() => setActiveDeal(d.id)}>
                <TableCell className="text-white font-medium">{d.company || "—"}</TableCell>
                <TableCell className="text-white/70">{d.contact || "—"}</TableCell>
                <TableCell className="text-[#2EE59D] tabular-nums">{fmtMoney(d.monthly_value)}</TableCell>
                <TableCell className="text-white/60 text-sm">{d.industry_label || "—"}</TableCell>
                <TableCell className="text-white/60 text-sm">{d.assigned_to_name || "—"}</TableCell>
                <TableCell className="text-right text-white/80 tabular-nums">{d.shortlisted_count || 0}</TableCell>
                <TableCell className="text-right">
                  {d.placed_count > 0
                    ? <Badge className={`border ${STAGE_COLOR.Placed}`}>{d.placed_count} placed</Badge>
                    : <span className="text-white/40">0</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Sheet open={!!activeDeal} onOpenChange={(o) => { if (!o) setActiveDeal(null); }}>
        {activeDeal && (
          <PlacementPanel
            token={token}
            dealId={activeDeal}
            stages={["Matched", "Shortlisted", "Interviewing", "Selected", "Placed"]}
            onClose={() => setActiveDeal(null)}
            onChanged={load}
          />
        )}
      </Sheet>
    </div>
  );
};

export default StaffingManager;
